import React, { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import Layout from "../../ui/Layout"
import NavMain from "../../app/NavMain"
import { Helmet } from "react-helmet-async"
import ScrollablePage from "../../ui/ScrollablePage"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner"
import { Action, Actions, Facts, Header, Overview } from "../../ui/PageHeader"
import UserEditable from "../../ui/UserEditable"
import Breadcrumbs from "../../ui/Breadcrumbs"
import IDCopy from "../../ui/IDCopy"
import Timestamp from "../../ui/Timestamp"
import ErrorMsg from "../../ui/ErrorMsg"
import Pill from "../../ui/Pill"
import Text from "../../ui/Text"
import theme from "../../ui/theme"
import {
  fetchUserAccountById,
  selectUpdatingUserAccount,
  selectUserAccountsById,
  setPasswordResetRequired,
  updateUserAccountActive,
  updateUserAccountDisplayName,
} from "./slice"
import { selectAdvancedMode } from "../settings/slice"
import ToggleSwitch from "../../ui/ToggleSwitch"
import styled from "styled-components"

import PropTypes from "prop-types"
import Button from "../../ui/Button"
import {
  faKey,
  faUserPlus,
  faShieldHalved,
} from "@fortawesome/free-solid-svg-icons"
import AddRole from "./AddRole"
import ChangePassword from "./ChangePassword"
import RolesTable from "./RolesTable"
import UserActivity from "./UserActivity"
import Authed from "./Authed"
import { Duration } from "luxon"
import Cancel2FA from "./Cancel2FA"
import Enroll2FA from "./Enroll2FA"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

const SwitchContainer = styled.div`
  width: 100%;
  display: flex;
  span {
    flex: auto;
    margin-right: 1rem;
  }
`

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`

Container.propTypes = { children: PropTypes.node }

const ACTION_ADD_ROLE = "addrole"
const ACTION_RESET_PWD = "resetpwd"
const ACTION_2FA_ENROLL = "2faenroll"
const ACTION_2FA_CANCEL = "2facancel"

const UserAccount = () => {
  const dispatch = useDispatch()
  const { id } = useParams()
  const advancedMode = useSelector(selectAdvancedMode)
  const updating = useSelector(selectUpdatingUserAccount)
  const accountsByID = useSelector(selectUserAccountsById)
  const account = accountsByID[id]

  const status = account?._loadStatus || account?._saveStatus

  const [action, setAction] = useState(null) // active panel dialog

  const tokenLifetime = account?.token_lifetime
    ? Duration.fromISO(account.token_lifetime)
    : null

  useEffect(() => {
    dispatch(fetchUserAccountById(id))
  }, [id])

  const handleSetActive = (id, checked, _etag) => {
    dispatch(updateUserAccountActive({ id, active: checked, _etag }))
  }

  const onDisplayNameChange = (name) => {
    return dispatch(
      updateUserAccountDisplayName({
        id: account.id,
        _etag: account._etag,
        name,
      })
    )
  }

  const handleForceResetPassword = () => {
    // TODO: Ask for confirmation?
    return dispatch(
      setPasswordResetRequired({
        ids: [account.id],
      })
    )
  }

  // Close side panel on ESC
  useEffect(() => {
    const handler = (e) => {
      if (e.key == "Escape") {
        setAction(null)
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  })

  return (
    <Layout>
      <NavMain />
      <Helmet>
        <title>User account: {account?.display_name || id} - Astrosite</title>
      </Helmet>
      <ScrollablePage>
        {(status === "loading" || updating?._saveStatus === "updating") && (
          <Backdrop>
            <LoadingSpinner />
          </Backdrop>
        )}
        <Header>
          <Overview>
            <h1>{account?.id}</h1>
            <Breadcrumbs>
              <Link to="/accounts?sort_by=id&sort_order=DESC">
                User accounts
              </Link>
              <IDCopy id={id} showCopy />
            </Breadcrumbs>
          </Overview>
          {status !== "loading" && (
            <>
              <Facts>
                <dt>Display name</dt>
                <dd>
                  <UserEditable
                    value={account?.display_name}
                    onChange={onDisplayNameChange}
                    disabled={status !== "idle"}
                  />
                </dd>
                <dt>Created</dt>
                <dd>
                  {account?.created_at && (
                    <Timestamp utc={advancedMode} columnMode={false}>
                      {account?.created_at}
                    </Timestamp>
                  )}
                </dd>
                <dt>Updated</dt>
                <dd>
                  {account?.updated_at && (
                    <Timestamp utc={advancedMode} columnMode={false}>
                      {account?.updated_at}
                    </Timestamp>
                  )}
                </dd>
                <dt>Last IP address</dt>
                <dd>{account?.last_ip_address}</dd>
                {tokenLifetime && (
                  <>
                    <dt>Login token active for</dt>
                    <dd>{tokenLifetime.toHuman()}</dd>
                  </>
                )}
                {account?.password_reset_required && (
                  <>
                    <dt>Security</dt>
                    <dd>
                      <Pill
                        fgCol={theme.colours.activenew}
                        title="User has to reset password the next time they log in"
                      >
                        password reset requested
                      </Pill>
                    </dd>
                  </>
                )}
                <dt>Two-factor auth</dt>
                <dd>
                  {account?.totp_required ? (
                    <Text active>
                      <FontAwesomeIcon icon={faShieldHalved} /> Enrolled
                    </Text>
                  ) : (
                    <Text>Not enrolled</Text>
                  )}
                </dd>
              </Facts>
              <Actions>
                <>
                  <Action>
                    <SwitchContainer>
                      <span>Active:</span>
                      <ToggleSwitch
                        handleChange={(checked) =>
                          handleSetActive(account.id, checked, account._etag)
                        }
                        checked={account?.is_active}
                        id="mode"
                      />
                    </SwitchContainer>
                  </Action>{" "}
                  <Action>
                    <Button
                      onClick={() => setAction(ACTION_ADD_ROLE)}
                      icon={faUserPlus}
                    >
                      Add role
                    </Button>
                  </Action>
                  <Action>
                    <Button
                      onClick={() => setAction(ACTION_RESET_PWD)}
                      icon={faKey}
                    >
                      Set password
                    </Button>
                  </Action>
                  <Authed allowRoles={["admin"]}>
                    {!account?.password_reset_required && (
                      <Action>
                        <Button onClick={handleForceResetPassword} icon={faKey}>
                          Force password reset
                        </Button>
                      </Action>
                    )}
                    {!account?.totp_required && (
                      <Action>
                        <Button
                          onClick={() => setAction(ACTION_2FA_ENROLL)}
                          icon={faShieldHalved}
                        >
                          Enroll for 2FA
                        </Button>
                      </Action>
                    )}
                    {account?.totp_required && (
                      <Action>
                        <Button
                          onClick={() => setAction(ACTION_2FA_CANCEL)}
                          icon={faShieldHalved}
                          delete
                        >
                          Cancel 2FA
                        </Button>
                      </Action>
                    )}
                  </Authed>
                </>
              </Actions>
            </>
          )}
        </Header>
        {status === "rejected" && (
          <ErrorMsg
            msg={`There is a problem fetching the user account with ID ${id}`}
            showIcon={true}
          />
        )}
        {updating?._saveStatus === "rejected" && (
          <ErrorMsg
            msg={`There is a problem with the request password reset for user account with ID ${id}`}
            showIcon={true}
          />
        )}
        <Container>
          <UserActivity id={id} />
          <RolesTable id={id} roles={account?.roles} />
        </Container>
      </ScrollablePage>
      {action === ACTION_ADD_ROLE && (
        <AddRole id={id} handleCancel={() => setAction(null)} />
      )}
      {action === ACTION_RESET_PWD && (
        <ChangePassword id={id} handleCancel={() => setAction(null)} />
      )}
      {action === ACTION_2FA_ENROLL && (
        <Enroll2FA id={id} handleCancel={() => setAction(null)} />
      )}
      {action === ACTION_2FA_CANCEL && (
        <Cancel2FA id={id} handleCancel={() => setAction(null)} />
      )}
    </Layout>
  )
}

export default UserAccount
